export const Roles = {
  MASTER_ADMIN: "master_admin",
  PLAN_ADMIN: "plan_admin",
};

// Mirrors authorizeRoles in VirtueHrm_Admin_BE/middleware/authMiddleware.js.
// plan_admin owns plans, plan features and subscriptions end to end, and is
// excluded from user administration, feature definitions and customer
// management. Two reads stay open so the granted modules remain usable: the
// feature catalog, for mapping plan features, and the customer list, which a
// subscription needs to resolve its customer. Neither earns the page itself.
//
// `payments` is withheld from plan_admin entirely. Everything else it owns is
// configuration; the payment endpoints take money from a customer and change
// what a customer owes, which is the Super Admin's remit. This map only
// decides what the console shows -- the backend enforces the same matrix in
// routes/paymentRoutes.js, and that is what actually holds.
const PERMISSIONS = {
  [Roles.MASTER_ADMIN]: {
    users: { read: true, write: true },
    customers: { read: true, write: true },
    customerConfigs: { read: true, write: true },
    customerHistory: { read: true, write: false },
    plans: { read: true, write: true },
    subscriptions: { read: true, write: true },
    features: { read: true, write: true },
    featureSettings: { read: true, write: true },
    planFeatures: { read: true, write: true },
    payments: { read: true, write: true },
  },
  [Roles.PLAN_ADMIN]: {
    users: { read: false, write: false },
    customers: { read: true, write: false },
    customerConfigs: { read: false, write: false },
    customerHistory: { read: false, write: false },
    plans: { read: true, write: true },
    subscriptions: { read: true, write: true },
    features: { read: true, write: false },
    featureSettings: { read: false, write: false },
    planFeatures: { read: true, write: true },
    payments: { read: false, write: false },
  },
};

const ROLE_LABELS = {
  [Roles.MASTER_ADMIN]: "Master Admin",
  [Roles.PLAN_ADMIN]: "Plan Admin",
};

const rolesOf = (user) => {
  if (user?.roles?.length) {
    return user.roles;
  }

  return user?.role ? [user.role] : [];
};

const can = (user, module, action) =>
  rolesOf(user).some((role) => PERMISSIONS[role]?.[module]?.[action] === true);

// The single source for showing a role to a person. Falls back to the raw
// value so an unmapped role is still visible rather than silently "Guest".
export const roleLabel = (user) => {
  const [role] = rolesOf(user);

  if (!role) {
    return "Guest";
  }

  return ROLE_LABELS[role] || role;
};

export const canRead = (user, module) => can(user, module, "read");

export const canWrite = (user, module) => can(user, module, "write");

// What each page requires to be opened. Most pages need read on their module.
// /features and /customers are the exceptions: both exist to author records,
// so they need write even though plan_admin can read either list as a lookup
// from inside plan features and subscriptions.
export const ROUTE_REQUIREMENTS = {
  "/dashboard": null,
  "/users": { module: "users", action: "read" },
  "/plans": { module: "plans", action: "read" },
  "/customers": { module: "customers", action: "write" },
  "/customers/add": { module: "customers", action: "write" },
  "/customers/edit": { module: "customers", action: "write" },
  "/customers/history": { module: "customerHistory", action: "read" },
  "/subscriptions": { module: "subscriptions", action: "read" },
  "/features": { module: "features", action: "write" },
  "/plan-features": { module: "planFeatures", action: "read" },
  "/payments": { module: "payments", action: "read" },
};

export const canAccessRoute = (user, routeKey) => {
  const requirement = ROUTE_REQUIREMENTS[routeKey];

  // A known role may always reach a page with no module behind it.
  if (requirement === null) {
    return rolesOf(user).some((role) => !!PERMISSIONS[role]);
  }

  return requirement ? can(user, requirement.module, requirement.action) : false;
};

// First page the user is allowed to see, used for post-login and fallback redirects.
export const defaultRouteFor = (user) =>
  canAccessRoute(user, "/dashboard") ? "/dashboard" : "/signin";
